// ── Google Calendar Sync ──────────────────────────────────────────────────────
const CLIENT_ID = '937593935273-97agsm49dv5j73sf39r7b9na7rc0l42j.apps.googleusercontent.com'
const SCOPES = 'https://www.googleapis.com/auth/calendar.events'
const CALENDAR_ID = 'primary'
const TOKEN_KEY = 'goog_cal_token'
const TOKEN_EXPIRY_KEY = 'goog_cal_expiry'
const JOB_MAP_KEY = 'goog_cal_job_map'

// ── Token ─────────────────────────────────────────────────────────────────────
function getToken() {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY)
  if (!expiry || Date.now() > parseInt(expiry)) {
    localStorage.removeItem(TOKEN_KEY)
    return null
  }
  return localStorage.getItem(TOKEN_KEY)
}

function saveToken(token, expiresIn) {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + expiresIn * 1000 - 60000))
}

export function isConnected() { return !!getToken() }

export function disconnectGoogleCalendar() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(TOKEN_EXPIRY_KEY)
  localStorage.removeItem(JOB_MAP_KEY)
}

// ── Event ID map ──────────────────────────────────────────────────────────────
function getMap() { try { return JSON.parse(localStorage.getItem(JOB_MAP_KEY)||'{}') } catch { return {} } }
function getEventId(jobId) { return getMap()[jobId] || null }
function saveEventId(jobId, eventId) { const m=getMap(); m[jobId]=eventId; localStorage.setItem(JOB_MAP_KEY,JSON.stringify(m)) }
function removeEventId(jobId) { const m=getMap(); delete m[jobId]; localStorage.setItem(JOB_MAP_KEY,JSON.stringify(m)) }

// ── OAuth popup ───────────────────────────────────────────────────────────────
export function connectGoogleCalendar() {
  return new Promise((resolve, reject) => {
    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      redirect_uri: window.location.origin,
      response_type: 'token',
      scope: SCOPES,
      prompt: 'select_account',
    })
    const popup = window.open(
      `https://accounts.google.com/o/oauth2/auth?${params}`,
      'google_auth', 'width=500,height=600,left=200,top=100'
    )
    const interval = setInterval(() => {
      try {
        if (!popup || popup.closed) { clearInterval(interval); reject(new Error('Popup closed')); return }
        const hash = popup.location.hash
        if (hash && hash.includes('access_token')) {
          clearInterval(interval)
          popup.close()
          const p = new URLSearchParams(hash.substring(1))
          const token = p.get('access_token')
          const expiresIn = parseInt(p.get('expires_in')) || 3600
          saveToken(token, expiresIn)
          resolve(token)
        }
      } catch(e) { /* cross-origin while on google.com — ignore */ }
    }, 500)
  })
}

// ── API helper ────────────────────────────────────────────────────────────────
async function gcal(method, path, body) {
  const token = getToken()
  if (!token) throw new Error('Not connected')
  const res = await fetch(`https://www.googleapis.com/calendar/v3${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  })
  if (res.status === 204) return null
  if (!res.ok) {
    const err = await res.json().catch(()=>({}))
    throw new Error(err?.error?.message || `HTTP ${res.status}`)
  }
  return res.json()
}

// ── Build event payload ───────────────────────────────────────────────────────
function buildEvent(job, client) {
  const clientName = typeof client === 'string' ? client : client.name
  const isAirbnb = typeof client === 'object' && client.is_airbnb
  const date = job.scheduled_date
  const address = job.one_time_address || (typeof client === 'object' ? (client.address ? `${client.address}${client.city ? ', ' + client.city : ''}` : '') : '')
  const title = `${job.not_same_day ? '* ' : ''}${clientName}${job.route ? ` [${job.route}]` : ''}${job.one_time_note ? ` — ${job.one_time_note}` : ''}`
  const desc = [
    `Kitty's Cleaning Services`,
    `Cliente: ${clientName}`,
    isAirbnb ? 'Airbnb Turnover' : '',
    address ? `Dirección: ${address}` : '',
    job.route ? `Equipo: ${job.route}` : '',
    job.hours ? `Duración: ${job.hours}h` : '',
    job.one_time_note ? `Nota: ${job.one_time_note}` : '',
    `job_id:${job.id}`,
  ].filter(Boolean).join('\n')

  if (job.start_time) {
    const [time, ampm] = job.start_time.split(' ')
    let [h, m] = time.split(':').map(Number)
    if (ampm === 'PM' && h !== 12) h += 12
    if (ampm === 'AM' && h === 12) h = 0
    const hours = parseFloat(job.hours) || 2
    const endH = h + Math.floor(hours)
    const endM = (m||0) + Math.round((hours % 1) * 60)
    const pad = n => String(n).padStart(2,'0')
    return {
      summary: title,
      description: desc,
      location: address || undefined,
      start: { dateTime: `${date}T${pad(h)}:${pad(m||0)}:00`, timeZone: 'America/Los_Angeles' },
      end:   { dateTime: `${date}T${pad(endH)}:${pad(endM)}:00`, timeZone: 'America/Los_Angeles' },
      colorId: isAirbnb ? '11' : '3',
      reminders: { useDefault: false, overrides: [{ method: 'popup', minutes: 60 }] },
    }
  }

  // All-day events: end date must be the day AFTER start date
  const nextDay = new Date(date + 'T12:00:00')
  nextDay.setDate(nextDay.getDate() + 1)
  const endDate = nextDay.toISOString().split('T')[0]

  return {
    summary: title,
    description: desc,
    location: address || undefined,
    start: { date },
    end:   { date: endDate },
    colorId: isAirbnb ? '11' : '3',
    reminders: { useDefault: false },
  }
}

// ── Public sync functions ─────────────────────────────────────────────────────
export async function syncJobCreate(job, client) {
  if (!isConnected()) return
  try {
    const dateMin = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()
    const dateMax = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString()
    const existingId = await findEventByJobId(job.id, dateMin, dateMax)
    if (existingId) {
      await gcal('PUT', `/calendars/${CALENDAR_ID}/events/${existingId}`, buildEvent(job, client))
    } else {
      const created = await gcal('POST', `/calendars/${CALENDAR_ID}/events`, buildEvent(job, client))
      if (created?.id) saveEventId(job.id, created.id)
    }
  } catch(e) { console.warn('GCal create failed:', e.message) }
}

export async function syncJobUpdate(job, client) {
  if (!isConnected()) return
  try {
    const payload = buildEvent(job, client)
    const dateMin = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()
    const dateMax = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString()
    const eventId = await findEventByJobId(job.id, dateMin, dateMax)
    if (eventId) {
      await gcal('PUT', `/calendars/${CALENDAR_ID}/events/${eventId}`, payload)
    } else {
      const created = await gcal('POST', `/calendars/${CALENDAR_ID}/events`, payload)
      if (created?.id) saveEventId(job.id, created.id)
    }
  } catch(e) { console.warn('GCal update failed:', e.message) }
}

export async function syncJobDelete(jobId) {
  if (!isConnected()) return
  try {
    const dateMin = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()
    const dateMax = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString()
    const eventId = await findEventByJobId(jobId, dateMin, dateMax)
    if (eventId) {
      await gcal('DELETE', `/calendars/${CALENDAR_ID}/events/${eventId}`)
      removeEventId(jobId)
    }
  } catch(e) { console.warn('GCal delete failed:', e.message) }
}

// ── Search GCal for existing event by job ID ──────────────────────────────────
async function findEventByJobId(jobId, dateMin, dateMax) {
  // First check localStorage map
  const cached = getEventId(jobId)
  if (cached) return cached
  // Search Google Calendar for event with this job_id in description
  const params = new URLSearchParams({
    q: `job_id:${jobId}`,
    timeMin: dateMin,
    timeMax: dateMax,
    singleEvents: 'true',
    maxResults: '5',
  })
  try {
    const res = await gcal('GET', `/calendars/${CALENDAR_ID}/events?${params}`)
    const match = res?.items?.find(e => e.description?.includes(`job_id:${jobId}`))
    if (match?.id) { saveEventId(jobId, match.id); return match.id }
  } catch(e) { /* ignore search errors */ }
  return null
}

export async function bulkSyncJobs(jobs, clients) {
  if (!isConnected()) return { synced: 0, failed: 0, errors: [] }
  let synced = 0, failed = 0
  const errors = []
  const dateMin = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
  const dateMax = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString()
  for (const job of jobs) {
    const client = clients.find(c => c.id === job.client_id)
    if (!client) { errors.push(`No client found for job ${job.id}`); continue }
    try {
      const payload = buildEvent(job, client)
      const existingEventId = await findEventByJobId(job.id, dateMin, dateMax)
      if (existingEventId) {
        await gcal('PUT', `/calendars/${CALENDAR_ID}/events/${existingEventId}`, payload)
        synced++
      } else {
        const created = await gcal('POST', `/calendars/${CALENDAR_ID}/events`, payload)
        if (created?.id) { saveEventId(job.id, created.id); synced++ }
      }
    } catch(e) {
      console.error('Bulk sync failed for', client.name, ':', e.message)
      errors.push(`${client.name}: ${e.message}`)
      failed++
    }
    await new Promise(r => setTimeout(r, 150))
  }
  return { synced, failed, errors }
}
